/**
 * ActiveCampaignsPanel — Executive Home focus lens on campaigns
 * requiring attention. Patch 36.0 · Project Obsidian.
 *
 * Filters and orders CampaignRun rows deterministically:
 *   1. Requires Action — health ∈ {blocked, partial, failed,
 *      promotion_incomplete, reconciliation_failed} OR blocker present.
 *   2. Recently Updated (last_activity_at DESC).
 *   3. Recently Created (started_at DESC) as tiebreaker.
 *
 * Completed campaigns (stage='published' or 'archived' with no blocker)
 * are hidden. Inactive campaigns with no activity in the last 30 days
 * are hidden.
 *
 * The frontend never invents state — every displayed row IS a canonical
 * CampaignRun projection returned by the backend.
 */
import { Link } from "react-router-dom";
import { Megaphone, ArrowRight } from "lucide-react";

import type { CampaignRun } from "@/entities/campaign-run";
import { useCampaignRuns } from "@/entities/campaign-run";
import { useCurrentWorkspace } from "@/entities/workspace";
import { Badge } from "@/shared/ui/badge";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

const ATTENTION_HEALTHS = new Set([
  "blocked",
  "partial",
  "failed",
  "promotion_incomplete",
  "reconciliation_failed",
]);
const HIDDEN_STAGES = new Set(["published", "archived"]);

function requiresAction(r: CampaignRun): boolean {
  if (r.blocker) return true;
  return ATTENTION_HEALTHS.has(r.health);
}

function isCompleted(r: CampaignRun): boolean {
  return HIDDEN_STAGES.has(r.stage) && !r.blocker;
}

function pickActive(runs: CampaignRun[]): CampaignRun[] {
  const filtered = runs.filter((r) => !isCompleted(r));
  filtered.sort((a, b) => {
    const aAction = requiresAction(a) ? 0 : 1;
    const bAction = requiresAction(b) ? 0 : 1;
    if (aAction !== bAction) return aAction - bAction;
    const aAct = a.last_activity_at ? Date.parse(a.last_activity_at) : 0;
    const bAct = b.last_activity_at ? Date.parse(b.last_activity_at) : 0;
    if (aAct !== bAct) return bAct - aAct;
    const aStart = a.started_at ? Date.parse(a.started_at) : 0;
    const bStart = b.started_at ? Date.parse(b.started_at) : 0;
    return bStart - aStart;
  });
  return filtered.slice(0, 4);
}

const STAGE_LABELS: Record<string, string> = {
  draft: "Draft",
  generating: "Generating",
  review: "In Review",
  approved: "Approved",
  scheduling: "Scheduling",
  scheduled: "Scheduled",
  publishing: "Publishing",
  published: "Published",
  archived: "Archived",
};
function humaniseStage(stage: string): string {
  return STAGE_LABELS[stage] ?? stage.replace(/_/g, " ");
}

export function ActiveCampaignsPanel(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const q = useCampaignRuns(workspace?.id ?? null, 50);

  const rows = q.data ? pickActive(q.data) : [];
  // React-Query v5: with enabled:false, ``isLoading`` is false and
  // ``isPending`` is true. Treat pre-hydration as loading so the
  // empty state doesn't flash before the workspace resolves.
  const isLoading = !workspace?.id || q.isPending || q.isLoading;

  return (
    <Card
      className="p-5 border-white/10 obsidian-card"
      data-testid="obsidian-active-campaigns"
      data-state={isLoading ? "loading" : rows.length === 0 ? "empty" : "populated"}
      data-count={rows.length}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
          <h3 className="text-sm font-semibold text-[var(--md-ivory-0)]">
            Active Campaigns
          </h3>
        </div>
        {rows.length > 0 ? (
          <Link
            to="/campaigns"
            data-testid="obsidian-active-campaigns-all"
            className="text-[11px] text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
          >
            View all →
          </Link>
        ) : null}
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="mt-4 space-y-2" data-testid="obsidian-active-campaigns-list">
          {rows.map((run) => (
            <CampaignRow key={run.id} run={run} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function CampaignRow({ run }: { run: CampaignRun }): JSX.Element {
  const attention = requiresAction(run);
  return (
    <li>
      <Link
        to={`/campaigns/${run.campaign_pack_id}`}
        data-testid={`obsidian-active-campaign-${run.campaign_pack_id}`}
        data-attention={attention ? "true" : "false"}
        className="group flex items-start justify-between gap-3 rounded-lg border border-white/5 bg-black/10 px-3 py-2.5 hover:border-white/15 hover:bg-white/[0.03] transition"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-[var(--md-ivory-0)] truncate">
              {run.campaign_name || "Untitled campaign"}
            </span>
            <Badge
              variant="outline"
              data-testid={`obsidian-active-campaign-${run.campaign_pack_id}-stage`}
              className="text-[10px]"
            >
              {humaniseStage(run.stage)}
            </Badge>
            {attention ? (
              <Badge
                variant="outline"
                data-testid={`obsidian-active-campaign-${run.campaign_pack_id}-attention`}
                className="text-[10px] border-amber-500/40 text-amber-400 bg-amber-500/10"
              >
                Requires Action
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-[11px] text-[var(--md-fog)]">
            {run.blocker
              ? run.blocker
              : `${run.counters.approved}/${run.counters.requested} approved · ${run.counters.published} published`}
          </p>
        </div>
        <ArrowRight
          className="h-3.5 w-3.5 text-[var(--md-fog)] mt-1 shrink-0 transition group-hover:text-[var(--md-ivory-0)] group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </li>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div
      data-testid="obsidian-active-campaigns-empty"
      className="mt-4 rounded-lg border border-white/5 bg-black/10 p-4"
    >
      <p className="text-sm font-medium text-[var(--md-ivory-0)]">
        You don&rsquo;t have an active campaign.
      </p>
      <p className="mt-1 text-xs text-[var(--md-fog)]">
        Start a campaign to begin generating approved marketing assets.
      </p>
      <Link
        to="/campaigns/new"
        data-testid="obsidian-active-campaigns-empty-cta"
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--md-ivory-0)] hover:text-[var(--brand-cyan)]"
      >
        Start a campaign
        <ArrowRight className="h-3 w-3" aria-hidden />
      </Link>
    </div>
  );
}
